<template>
    <v-img
        :src="checkImagePathLogo(GET_PREFERENCES.find(x => x.parameter == 'system_app_image')?.value ?? null)"
        class="background-image"
        style="width: 100%; height: 100vh; object-fit: cover;"
    >
        <v-container fluid class="fill-height d-flex align-center justify-center">
            <v-dialog v-model="uploadModalOpen" max-width="440" persistent>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn
                        @click="openDialog"
                        elevation="24"
                        color="white"
                        icon rounded fab plain
                        v-bind="attrs"
                        v-on="on"
                        class="d-flex flex-column align-center justify-center"
                    >
                        <v-icon size="35" left>mdi-qrcode-scan</v-icon>
                        <span style="font-family: Evogria; font-size: 1.8rem; font-weight: bold; color: white">
                            SCAN QR to Register
                        </span>
                    </v-btn>
                </template>

                <v-card class="guest-dialog-card" elevation="24">

                    <!-- ── SCAN TYPE TOGGLE (always visible in header area) ── -->
                    <div class="scan-type-toggle-bar">
                        <v-btn-toggle v-model="scanType" mandatory color="#17468f" dense>
                            <v-btn value="company" small class="scan-type-btn">
                                <v-icon left x-small>mdi-office-building</v-icon>
                                By Company
                            </v-btn>
                            <v-btn value="guest" small class="scan-type-btn">
                                <v-icon left x-small>mdi-badge-account-outline</v-icon>
                                By Guest
                            </v-btn>
                        </v-btn-toggle>
                    </div>

                    <!-- ══════════════════════════════════════════════════ -->
                    <!-- ── BY COMPANY FLOW ── -->
                    <!-- ══════════════════════════════════════════════════ -->
                    <template v-if="scanType === 'company'">

                        <!-- ── SCAN SECTION ── -->
                        <template v-if="!companyCode">
                            <div class="guest-dialog-header">
                                <div class="guest-dialog-icon-wrap">
                                    <v-icon size="38" color="white">mdi-qrcode-scan</v-icon>
                                </div>
                                <div class="guest-dialog-header-text">
                                    <div class="guest-dialog-title">Scan Company QR</div>
                                    <div class="guest-dialog-subtitle">Point camera at the company QR code</div>
                                </div>
                            </div>

                            <!-- Scanner attaches here -->
                            <div id="qr-code-full-region" style="width:100%;"></div>

                            <div class="guest-scan-status my-2">
                                <v-icon size="16" color="#4fc3f7" class="mr-1">mdi-line-scan</v-icon>
                                <span>Align the QR code within the frame</span>
                            </div>

                            <v-card-text class="pt-0 pb-2 px-6">                 
                                <!-- OR SELECT divider -->
                                <div class="scan-divider-row my-3">
                                    <v-divider dark></v-divider>
                                    <span class="scan-divider-text">OR SELECT</span>
                                    <v-divider dark></v-divider>
                                </div>

                                <!-- Company name dropdown -->
                                <v-autocomplete
                                    v-model="selectedCompanyCode"
                                    :items="dropdowns.company_list"
                                    item-text="name"
                                    item-value="code"
                                    label="Select Company"
                                    outlined
                                    clearable
                                    color="#4fc3f7"
                                    prepend-inner-icon="mdi-office-building"
                                    hide-details
                                    dark
                                    class="dialog-input-field"
                                    :search-input.sync="companySearchInput"
                                    @click="companySearchInput = ''"
                                    @change="onCompanySelect"
                                ></v-autocomplete>
                            </v-card-text>

                            <v-card-actions class="px-6 pb-5 pt-2">
                                <v-btn @click="closeDialog" outlined color="grey darken-1" class="guest-dialog-cancel-btn">
                                    <v-icon left small>mdi-close</v-icon>
                                    Close
                                </v-btn>
                                <v-spacer></v-spacer>
                                <v-btn
                                    @click="submitCompanyCode"
                                    :loading="submittingCompanyCode"
                                    :disabled="companyCodeValue.length < 6"
                                    color="#17468f"
                                    dark depressed
                                    class="guest-dialog-submit-btn"
                                >
                                    <v-icon left small>mdi-check-circle-outline</v-icon>
                                    Submit
                                </v-btn>
                            </v-card-actions>
                        </template>

                        <!-- ── STEP 2: GUEST SELECTION ── -->
                        <template v-else-if="companyCode && !paxConfirmed">
                            <div class="guest-dialog-header">
                                <div class="guest-dialog-icon-wrap">
                                    <v-icon size="38" color="white">mdi-account-group</v-icon>
                                </div>
                                <div class="guest-dialog-header-text">
                                    <div class="guest-dialog-title">Select Attending Guests</div>
                                    <div class="guest-dialog-subtitle">Tap guests to confirm attendance, then Continue</div>
                                </div>
                            </div>

                            <div class="guest-dialog-company-row">
                                <v-icon size="16" color="#4fc3f7" class="mr-1">mdi-office-building</v-icon>
                                <span class="guest-dialog-company-label">Company:</span>
                                <v-chip small color="#17468f" dark class="ml-2 font-weight-bold" style="letter-spacing:0.5px;">
                                    {{ selectedCompanyName }}
                                </v-chip>
                            </div>

                            <v-card-text class="pt-3 pb-2 px-6">
                                <!-- Pax limit indicator -->
                                <div v-if="companyNoOfTickets > 0" class="pax-limit-row mb-3">
                                    <v-chip x-small class="pax-limit-chip pax-limit-chip--total mr-1">
                                        <v-icon left x-small>mdi-ticket-confirmation-outline</v-icon>
                                        Limit: {{ companyNoOfTickets }}
                                    </v-chip>
                                    <v-chip x-small class="pax-limit-chip pax-limit-chip--registered mr-1">
                                        <v-icon left x-small>mdi-account-check</v-icon>
                                        Registered: {{ companyExistingGuests.length }}
                                    </v-chip>
                                    <v-chip
                                        x-small
                                        :class="['pax-limit-chip', availableSlots > 0 ? 'pax-limit-chip--available' : 'pax-limit-chip--full']"
                                    >
                                        <v-icon left x-small>{{ availableSlots > 0 ? 'mdi-account-plus-outline' : 'mdi-account-off-outline' }}</v-icon>
                                        {{ availableSlots > 0 ? 'Walk-ins: ' + availableSlots : 'FULL' }}
                                    </v-chip>
                                </div>

                                <!-- Pre-registered guests list -->
                                <div v-if="companyExistingGuests.length > 0" class="existing-guests-wrap">
                                    <div class="existing-guests-label">
                                        <v-icon size="13" color="#4fc3f7" class="mr-1">mdi-account-check-outline</v-icon>
                                        Pre-registered guests ({{ companyExistingGuests.length }})
                                    </div>
                                    <div class="existing-guests-list">
                                        <div
                                            v-for="(guest, idx) in companyExistingGuests"
                                            :key="idx"
                                            class="existing-guest-item"
                                            :class="{
                                                'existing-guest-item--pending': guest.status_id != 2,
                                                'existing-guest-item--selected': selectedGuestIds.includes(guest.id)
                                            }"
                                            @click="guest.status_id != 2 && toggleGuestSelection(guest)"
                                        >
                                            <v-icon size="12" class="mr-1"
                                                :color="selectedGuestIds.includes(guest.id) ? '#81c784' : (guest.status_id == 2 ? '#4caf50' : '#ffa726')"
                                            >
                                                {{ selectedGuestIds.includes(guest.id) ? 'mdi-checkbox-marked-circle' : (guest.status_id == 2 ? 'mdi-check-circle' : 'mdi-clock-outline') }}
                                            </v-icon>
                                            {{ guest.full_name }}
                                            <v-spacer></v-spacer>
                                            <v-icon v-if="guest.status_id != 2" size="11"
                                                :color="selectedGuestIds.includes(guest.id) ? '#81c784' : 'rgba(255,165,0,0.5)'"
                                            >
                                                {{ selectedGuestIds.includes(guest.id) ? 'mdi-check' : 'mdi-plus' }}
                                            </v-icon>
                                        </div>
                                    </div>
                                </div>
                                <div v-else-if="loadingExistingGuests" class="existing-guests-wrap" style="text-align:center; padding:10px 0;">
                                    <v-progress-circular indeterminate color="#4fc3f7" size="22"></v-progress-circular>
                                </div>
                                <div v-else class="existing-guests-wrap" style="text-align:center; padding:8px 0; font-size:0.82rem; color:rgba(255,255,255,0.45);">
                                    <v-icon size="14" color="rgba(255,255,255,0.3)" class="mr-1">mdi-information-outline</v-icon>
                                    No pre-registered guests. Click Continue to add walk-ins.
                                </div>
                            </v-card-text>

                            <v-card-actions class="px-6 pb-5 pt-2">
                                <v-btn @click="resetToScan" outlined color="grey darken-1" class="guest-dialog-cancel-btn">
                                    <v-icon left small>mdi-arrow-left</v-icon>
                                    Back
                                </v-btn>
                                <v-spacer></v-spacer>
                                <v-btn
                                    @click="confirmPax"
                                    :loading="confirmingPendingGuest"
                                    color="#17468f"
                                    dark depressed
                                    class="guest-dialog-submit-btn"
                                >
                                    <v-icon left small>mdi-arrow-right-circle-outline</v-icon>
                                    Continue{{ selectedGuestIds.length > 0 ? ` (${selectedGuestIds.length} selected)` : '' }}
                                </v-btn>
                            </v-card-actions>
                        </template>

                        <!-- ── STEP 3: GUEST NAME ENTRY ── -->
                        <template v-else-if="companyCode && paxConfirmed">
                            <div class="guest-dialog-header">
                                <div class="guest-dialog-icon-wrap">
                                    <v-icon size="38" color="white">mdi-account-plus</v-icon>
                                </div>
                                <div class="guest-dialog-header-text">
                                    <div class="guest-dialog-title">Walk-in Registration</div>
                                    <div class="guest-dialog-subtitle">Enter names of guests not on the list</div>
                                </div>
                            </div>

                            <!-- Company row -->
                            <div class="guest-dialog-company-row">
                                <v-icon size="16" color="#4fc3f7" class="mr-1">mdi-office-building</v-icon>
                                <span class="guest-dialog-company-label">Company:</span>
                                <v-chip small color="#17468f" dark class="ml-2 font-weight-bold" style="letter-spacing:0.5px;">
                                    {{ selectedCompanyName }}
                                </v-chip>
                                <v-spacer></v-spacer>
                                <v-chip x-small color="rgba(79,195,247,0.15)" dark style="font-size:0.7rem;">
                                    <v-icon left x-small>mdi-account-plus-outline</v-icon>
                                    {{ registeredGuests.length }} added
                                </v-chip>
                            </div>

                            <v-card-text class="pt-4 pb-2 px-6">

                                <div class="name-guide-label mb-2">
                                    <v-icon size="14" color="#4fc3f7" class="mr-1">mdi-information-outline</v-icon>
                                    <span>Format: <strong>Last Name, First Name, Middle Name</strong></span>
                                </div>
                                <v-text-field
                                    ref="guestNameField"
                                    v-model="guestName"
                                    label="Full Name"
                                    placeholder="e.g. Dela Cruz, Juan, Santos"
                                    outlined
                                    :disabled="submittingRegistration || confirmingPendingGuest"
                                    @keyup.enter="handleGuestNameEnter"
                                    color="#4fc3f7"
                                    prepend-inner-icon="mdi-account-outline"
                                    hide-details="auto"
                                    class="guest-name-field dialog-input-field"
                                    dark
                                ></v-text-field>

                                <!-- Parsed name preview -->
                                <div v-if="parsedName.lastName || parsedName.firstName" class="parsed-name-preview mt-2">
                                    <div class="parsed-name-row">
                                        <span class="parsed-name-label">Last Name</span>
                                        <span class="parsed-name-value">{{ parsedName.lastName || '—' }}</span>
                                    </div>
                                    <div class="parsed-name-row">
                                        <span class="parsed-name-label">First Name</span>
                                        <span class="parsed-name-value">{{ parsedName.firstName || '—' }}</span>
                                    </div>
                                    <div class="parsed-name-row">
                                        <span class="parsed-name-label">Middle Name</span>
                                        <span class="parsed-name-value">{{ parsedName.middleName || '—' }}</span>
                                    </div>
                                </div>

                                <!-- Unified guest roster -->
                                <div
                                    v-if="registeredGuests.length > 0 || companyExistingGuests.length > 0"
                                    class="guest-roster mt-3"
                                >
                                    <div class="guest-roster-header">
                                        <v-icon size="13" color="#4fc3f7" class="mr-1">mdi-account-group-outline</v-icon>
                                        {{ selectedCompanyName }} — Guest List
                                        <span class="guest-roster-count ml-1">({{ registeredGuests.length + companyExistingGuests.length }})</span>
                                    </div>

                                    <div class="guest-roster-list">
                                        <!-- Registered THIS session -->
                                        <div
                                            v-for="(guest, idx) in registeredGuests"
                                            :key="'new-' + idx"
                                            class="guest-roster-item guest-roster-new"
                                        >
                                            <div class="guest-roster-badge guest-roster-badge-new">NEW</div>
                                            <v-icon size="12" color="#4caf50" class="mr-1">mdi-account-check</v-icon>
                                            <span class="guest-roster-name">{{ guest }}</span>
                                        </div>

                                        <!-- Already registered BEFORE this session -->
                                        <div
                                            v-for="(guest, idx) in companyExistingGuests"
                                            :key="'prev-' + idx"
                                            class="guest-roster-item guest-roster-prev"
                                            :class="{
                                                'guest-roster-item--pending': guest.status_id != 2,
                                                'guest-roster-item--confirming': confirmingPendingGuest && selectedPendingGuest && selectedPendingGuest.id === guest.id
                                            }"
                                            @click="guest.status_id != 2 && !confirmingPendingGuest && !submittingRegistration && confirmPreRegisteredGuest(guest)"
                                        >
                                            <div class="guest-roster-badge" :class="guest.status_id == 2 ? 'guest-roster-badge-prev' : 'guest-roster-badge-pending'">
                                                {{ guest.status_id == 2 ? 'REG' : 'TAP' }}
                                            </div>
                                            <v-icon size="12" :color="guest.status_id == 2 ? '#90a4ae' : '#81c784'" class="mr-1">
                                                {{ guest.status_id == 2 ? 'mdi-account-check' : 'mdi-account-clock-outline' }}
                                            </v-icon>
                                            <span class="guest-roster-name">{{ guest.full_name }}</span>
                                            <v-spacer></v-spacer>
                                            <v-progress-circular
                                                v-if="confirmingPendingGuest && selectedPendingGuest && selectedPendingGuest.id === guest.id"
                                                indeterminate size="12" width="2" color="#81c784"
                                            ></v-progress-circular>
                                            <v-icon v-else-if="guest.status_id != 2" size="12" color="rgba(129,199,132,0.5)">mdi-chevron-right</v-icon>
                                        </div>
                                    </div>
                                </div>

                                <div class="guest-dialog-hint mt-2">
                                    <v-icon size="13" color="#4fc3f7">mdi-information-outline</v-icon>
                                    Press <kbd>Enter</kbd> with a name to add more · press <kbd>Enter</kbd> on empty to finish
                                </div>
                            </v-card-text>

                            <v-card-actions class="px-6 pb-5 pt-3">
                                <v-btn
                                    @click="cancelRegistrationSession"
                                    :disabled="submittingRegistration"
                                    outlined
                                    color="grey darken-1"
                                    class="guest-dialog-cancel-btn"
                                >
                                    <v-icon left small>mdi-arrow-left</v-icon>
                                    Cancel
                                </v-btn>
                                <v-spacer></v-spacer>
                                <v-btn
                                    v-if="parsedName.firstName || parsedName.lastName"
                                    @click="submitCompanyRegistration(false)"
                                    :loading="submittingRegistration"
                                    :disabled="confirmingPendingGuest"
                                    color="rgba(23,70,143,0.7)"
                                    dark depressed
                                    class="guest-dialog-submit-btn mr-2"
                                >
                                    <v-icon left small>mdi-account-plus-outline</v-icon>
                                    Register &amp; Next
                                </v-btn>
                                <v-btn
                                    @click="submitCompanyRegistration(true)"
                                    :loading="submittingRegistration"
                                    :disabled="confirmingPendingGuest"
                                    color="#17468f"
                                    dark depressed
                                    class="guest-dialog-submit-btn"
                                >
                                    <v-icon left small>mdi-check-circle-outline</v-icon>
                                    {{ parsedName.firstName || parsedName.lastName ? 'Register & Finish' : 'Finish' }}
                                </v-btn>
                            </v-card-actions>
                        </template>

                    </template>

                    <!-- ══════════════════════════════════════════════════ -->
                    <!-- ── BY GUEST FLOW ── -->
                    <!-- ══════════════════════════════════════════════════ -->
                    <template v-else>

                        <!-- Header -->
                        <div class="guest-dialog-header guest-scan-header">
                            <div class="guest-dialog-icon-wrap">
                                <v-icon size="38" color="white">mdi-badge-account-outline</v-icon>
                            </div>
                            <div class="guest-dialog-header-text">
                                <div class="guest-dialog-title">Scan Your QR Code</div>
                                <div class="guest-dialog-subtitle">Use camera or enter unique code manually</div>
                            </div>
                        </div>

                        <!-- Scanner region (full-bleed, matches By Company width) -->
                        <div id="qr-code-guest-region" style="width:100%;"></div>

                        <div class="guest-scan-status my-2">
                            <v-icon size="16" color="#4fc3f7" class="mr-1">mdi-line-scan</v-icon>
                            <span>Align the QR code within the frame</span>
                        </div>

                        <v-card-text class="pt-0 pb-2 px-6">

                            <!-- OR divider -->
                            <div class="scan-divider-row my-3">
                                <v-divider dark></v-divider>
                                <span class="scan-divider-text">OR</span>
                                <v-divider dark></v-divider>
                            </div>

                            <!-- Unique code input boxes -->
                            <div class="unique-code-label mb-3">INPUT UNIQUE CODE</div>
                            <div class="unique-code-boxes">
                                <input
                                    v-for="(digit, idx) in uniqueCodeDigits"
                                    :key="idx"
                                    :ref="'ucBox' + idx"
                                    v-model="uniqueCodeDigits[idx]"
                                    class="unique-code-box"
                                    maxlength="1"
                                    @input="onUniqueCodeInput(idx)"
                                    @keydown.backspace="onUniqueCodeBackspace(idx, $event)"
                                    @keydown.enter="submitUniqueCode"
                                    @paste.prevent="onUniqueCodePaste($event)"
                                    autocomplete="off"
                                    spellcheck="false"
                                />
                            </div>

                        </v-card-text>

                        <v-card-actions class="px-6 pb-5 pt-2">
                            <v-btn @click="closeDialog" outlined color="grey darken-1" class="guest-dialog-cancel-btn">
                                <v-icon left small>mdi-close</v-icon>
                                Close
                            </v-btn>
                            <v-spacer></v-spacer>
                            <v-btn
                                @click="submitUniqueCode"
                                :loading="submittingGuestCode"
                                :disabled="uniqueCodeValue.length < 6"
                                color="#17468f"
                                dark depressed
                                class="guest-dialog-submit-btn"
                            >
                                <v-icon left small>mdi-check-circle-outline</v-icon>
                                Submit
                            </v-btn>
                        </v-card-actions>

                    </template>

                </v-card>
            </v-dialog>
        </v-container>
    </v-img>

</template>

<script>
	import Swal from 'sweetalert2';
	import axios from 'axios';
	import { Html5Qrcode } from 'html5-qrcode';
	import jsQR from 'jsqr';
    import { mapGetters } from 'vuex';

	export default {
	name: 'HelloWorld',
	data() {
		return {
		employeeId: '',
		remainingTickets: '',
		Html5Qrcode: null,
		scannedqrcodes: '',
		qrScannerRunning: false,
		dialog: false,
		uploadModalOpen: false,
		uploadFileModalOpen: false,
		selectedFile: null,
		selectedFileName: null,
		hovered: false,
        unique_code: '',
        submitting: false,
        settings: {
            confirm_timeout: 2000,
        },
        companyCode: '',
        guestName: '',
        nameDialog: false,
        submittingRegistration: false,
        selectedCompanyCode: null,
        selectedCompanyName: '',
        companySearchInput: null,
        paxCount: 1,
        paxConfirmed: false,
        currentPax: 1,
        registeredGuests: [],
        companyExistingGuests: [],
        loadingExistingGuests: false,
        companyNoOfTickets: 0,
        companyAttendedCount: 0,
        scanReady: false,
        scanType: 'company',
        guestScanReady: false,
        guestCameraActive: false,
        submittingGuestCode: false,
        submittingCompanyCode: false,
        uniqueCodeDigits: ['', '', '', '', '', ''],
        companyCodeDigits: ['', '', '', '', '', ''],
        dropdowns: {
            company_list: [],
        },
        selectedPendingGuest: null,
        confirmingPendingGuest: false,
        selectedGuestIds: [],
	};
	},
	beforeRouteLeave(to, from, next) {
        next()
	},
    computed: {
		...mapGetters(['GET_PARTICIPANT_LIST', 'GET_EVENT_ID', 'DIALOGS','GET_PREFERENCES','GET_NEW_MASTER_DATA']),

        parsedName() {
            const parts = this.guestName.split(',').map(part => part.trim());
            return {
                lastName:   parts[0] || '',
                firstName:  parts[1] || '',
                middleName: parts[2] || '',
            };
        },

        uniqueCodeValue() {
            return this.uniqueCodeDigits.join('');
        },

        companyCodeValue() {
            return this.companyCodeDigits.join('');
        },

        availableSlots() {
            if (!this.companyNoOfTickets) return null;
            return Math.max(0, this.companyNoOfTickets - this.companyExistingGuests.length);
        },

        pendingGuests() {
            return this.companyExistingGuests.filter(g => g.status_id != 2);
        },
	},
	watch: {
        scanType(newType) {
            this._stopScanner();
            this._stopGuestScanner();
            this.guestCameraActive = false;
            this.uniqueCodeDigits = ['', '', '', '', '', ''];
            this.companyCodeDigits = ['', '', '', '', '', ''];
            this._resetScanState();
            this.$nextTick(() => {
                if (newType === 'company') {
                    setTimeout(() => this._startScanner(false), 400);
                } else {
                    this.guestCameraActive = true;
                    setTimeout(() => this._startGuestScanner(false), 400);
                }
            });
        },
    },
	methods: {
        openDialog() {
            this.guestCameraActive = false;
            this.uniqueCodeDigits = ['', '', '', '', '', ''];
            this.companyCodeDigits = ['', '', '', '', '', ''];
            this._resetScanState();
            this.$nextTick(() => {
                if (this.scanType === 'company') {
                    setTimeout(() => this._startScanner(false), 400);
                } else {
                    this.guestCameraActive = true;
                    setTimeout(() => this._startGuestScanner(false), 400);
                }
            });
        },

        closeDialog() {
            this._stopScanner();
            this._stopGuestScanner();
            this._resetScanState();
            this.guestCameraActive = false;
            this.uniqueCodeDigits = ['', '', '', '', '', ''];
            this.companyCodeDigits = ['', '', '', '', '', ''];
            this.companySearchInput = null;
            this.uploadModalOpen = false;
        },

        resetToScan() {
            this.companyCodeDigits = ['', '', '', '', '', ''];
            this._resetScanState();
            this.$nextTick(() => {
                setTimeout(() => this._startScanner(false), 400);
            });
        },

        _resetScanState() {
            this.companyCode = '';
            this.selectedCompanyName = '';
            this.guestName = '';
            this.qrScannerRunning = false;
            this.scanReady = false;
            this.paxCount = 1;
            this.paxConfirmed = false;
            this.currentPax = 1;
            this.registeredGuests = [];
            this.companyExistingGuests = [];
            this.loadingExistingGuests = false;
            this.companyNoOfTickets = 0;
            this.companyAttendedCount = 0;
            this.selectedPendingGuest = null;
            this.confirmingPendingGuest = false;
            this.selectedGuestIds = [];
        },

        _stopScanner() {
            if (this.html5QrCode) {
                try { this.html5QrCode.stop(); } catch (_err) {}
                this.html5QrCode = null;
            }
            this.qrScannerRunning = false;
        },

        _stopGuestScanner() {
            if (this.html5QrCodeGuest) {
                try { this.html5QrCodeGuest.stop(); } catch (_err) {}
                this.html5QrCodeGuest = null;
            }
            this.guestScanReady = false;
        },

        submitEmployeeId(scannedCode) {
            this._stopScanner();

            const company = this.dropdowns.company_list.find(comp => comp.code === scannedCode);

            if (!company) {
                Swal.fire({
                    title: '<strong><u>INVALID QR CODE</u></strong>',
                    text: 'No company found with that code.',
                    icon: 'warning',
                    customClass: { confirmButton: 'orange-button' },
                }).then(() => {
                    this.$nextTick(() => setTimeout(() => this._startScanner(false), 400));
                });
                return;
            }

            this.companyCode = scannedCode;
            this.selectedCompanyName = company.name;
            this.guestName = '';
            this.fetchExistingGuests(scannedCode);
            this.$nextTick(() => {
                if (this.$refs.paxField) this.$refs.paxField.focus();
            });
        },

        onCompanySelect(code) {
            if (!code) return;
            this.$nextTick(() => { this.selectedCompanyCode = null; });
            this.submitEmployeeId(code);
        },

        enforcePaxRange() {
            const val = parseInt(this.paxCount, 10);
            if (isNaN(val) || val < 0) this.paxCount = 0;
            else if (val > 99) this.paxCount = 99;
            else this.paxCount = val;
        },

        async fetchExistingGuests(companyCode) {
            this.loadingExistingGuests = true;
            this.companyExistingGuests = [];
            try {
                const response = await axios.get(process.env.VUE_APP_API_HOST + '/api/guestsByCompanyCode', {
                    params: { company_code: companyCode, event_id: this.$store?.getters?.GET_EVENT_ID || 1 },
                });
                this.companyExistingGuests = response.data.guests || [];
                this.companyNoOfTickets = response.data.max_pax || 0;
                this.companyAttendedCount = response.data.attended_count || 0;
            } catch (error) {
                console.error('Failed to fetch existing guests', error);
            } finally {
                this.loadingExistingGuests = false;
            }
        },

        toggleGuestSelection(guest) {
            const idx = this.selectedGuestIds.indexOf(guest.id);
            if (idx === -1) {
                this.selectedGuestIds.push(guest.id);
            } else {
                this.selectedGuestIds.splice(idx, 1);
            }
        },

        async confirmPax() {
            // Confirm attendance for all selected pre-registered guests
            if (this.selectedGuestIds.length > 0) {
                this.confirmingPendingGuest = true;
                const guestsToConfirm = this.companyExistingGuests.filter(g => this.selectedGuestIds.includes(g.id));
                try {
                    for (const guest of guestsToConfirm) {
                        await axios.post(process.env.VUE_APP_API_HOST + '/api/registerGuestByQr', {
                            unique_code: guest.unique_code,
                        });
                        const i = this.companyExistingGuests.findIndex(g => g.id === guest.id);
                        if (i !== -1) this.$set(this.companyExistingGuests[i], 'status_id', 2);
                        this.companyAttendedCount++;
                    }
                } catch (error) {
                    await this._handleGuestQrError(error);
                    this.confirmingPendingGuest = false;
                    return;
                }
                this.confirmingPendingGuest = false;
                this.selectedGuestIds = [];
            }

            // Advance to the walk-in name entry step
            this.paxConfirmed = true;
            this.currentPax = 1;
            this.registeredGuests = [];
            this.guestName = '';
            this.$nextTick(() => {
                if (this.$refs.guestNameField) this.$refs.guestNameField.focus();
            });
        },

        async confirmPreRegisteredGuest(guest) {
            if (!guest || !guest.unique_code) return;

            this.selectedPendingGuest = guest;
            this.confirmingPendingGuest = true;
            try {
                await axios.post(process.env.VUE_APP_API_HOST + '/api/registerGuestByQr', {
                    unique_code: guest.unique_code,
                });

                // Mark the guest as attended in the local list
                const idx = this.companyExistingGuests.findIndex(g => g.id === guest.id);
                if (idx !== -1) this.$set(this.companyExistingGuests[idx], 'status_id', 2);

                this.companyAttendedCount++;

                // If we're in Step 3, add to the roster and keep the form open
                if (this.paxConfirmed) {
                    this.registeredGuests.push(guest.full_name);
                    this.guestName = '';
                    this.currentPax++;
                    this.$nextTick(() => {
                        if (this.$refs.guestNameField) this.$refs.guestNameField.focus();
                    });
                }
            } catch (error) {
                await this._handleGuestQrError(error);
            } finally {
                this.selectedPendingGuest = null;
                this.confirmingPendingGuest = false;
            }
        },

        async cancelRegistrationSession() {
            if (this.registeredGuests.length === 0) {
                this.resetToScan();
                return;
            }

            const registered = this.registeredGuests.length;
            const listHtml = this.registeredGuests
                .map((name, idx) => `<div style="text-align:left; padding:2px 0; font-size:0.85rem;">
                    <span style="color:#4caf50; font-weight:700;">${idx + 1}.</span> ${name}
                </div>`)
                .join('');

            const result = await Swal.fire({
                title: 'Cancel Registration?',
                html: `
                    <div style="text-align:center; margin-bottom:10px; font-size:0.95rem; color:#fff;">
                        <strong style="color:#4fc3f7;">${registered}</strong>
                        walk-in guest${registered > 1 ? 's' : ''} already registered this session.
                    </div>
                    <div style="background:rgba(23,70,143,0.15); border:1px solid rgba(79,195,247,0.15); border-radius:8px; padding:8px 12px; max-height:140px; overflow-y:auto; text-align:left;">
                        <div style="font-size:0.7rem; font-weight:700; color:#4caf50; letter-spacing:0.8px; text-transform:uppercase; margin-bottom:6px;">Registered:</div>
                        ${listHtml}
                    </div>
                `,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, Cancel',
                cancelButtonText: 'Continue Registering',
                confirmButtonColor: '#c62828',
                cancelButtonColor: '#17468f',
                reverseButtons: true,
            });

            if (result.isConfirmed) {
                this.resetToScan();
            }
        },

        handleGuestNameEnter() {
            if (this.submittingRegistration || this.confirmingPendingGuest) return;
            const hasName = this.parsedName.firstName.trim() || this.parsedName.lastName.trim();
            this.submitCompanyRegistration(!hasName);
        },

        async submitCompanyRegistration(andFinish = false) {
            const { lastName, firstName, middleName } = this.parsedName;
            const hasName = firstName.trim() || lastName.trim();

            // "Register & Next" requires a name; "Finish" without a name just ends the session
            if (!hasName && !andFinish) {
                Swal.fire({
                    title: 'Name Required',
                    text: 'Please enter the guest name in format: Last Name, First Name, Middle Name',
                    icon: 'warning',
                    customClass: { confirmButton: 'orange-button' },
                });
                return;
            }

            if (hasName) {
                this.submittingRegistration = true;
                try {
                    const response = await axios.post(process.env.VUE_APP_API_HOST + '/api/registerGuestByCompanyQr', {
                        company_code: this.companyCode,
                        first_name:   firstName.toUpperCase(),
                        last_name:    lastName.toUpperCase(),
                        middle_name:  middleName.toUpperCase(),
                    });

                    const guestLabel = `${lastName.toUpperCase()}, ${firstName.toUpperCase()}${middleName ? ', ' + middleName.toUpperCase() : ''}`;
                    this.registeredGuests.push(guestLabel);

                    if (!andFinish) {
                        // Brief success toast then stay on the form for the next walk-in
                        Swal.fire({
                            title: `Walk-in Registered!`,
                            html: `<div style="text-align:center;"><strong>${guestLabel}</strong><br><span style="font-size:0.85rem;opacity:0.7;">${response.data.company_name}</span></div>`,
                            icon: 'success',
                            timer: 1400,
                            timerProgressBar: true,
                            showConfirmButton: false,
                            customClass: { confirmButton: 'orange-button' },
                        }).then(() => {
                            this.guestName = '';
                            this.currentPax++;
                            this.$nextTick(() => {
                                if (this.$refs.guestNameField) this.$refs.guestNameField.focus();
                            });
                        });
                        this.submittingRegistration = false;
                        return;
                    }
                } catch (error) {
                    console.error(error);
                    const isPaxLimitExceeded = error?.response?.status === 422 &&
                        error?.response?.data?.error_code === 'PAX_LIMIT_EXCEEDED';

                    if (isPaxLimitExceeded) {
                        const d = error.response.data;
                        await Swal.fire({
                            title: 'Company Limit Reached!',
                            html: `<div style="text-align:center;">
                                <strong style="color:#ef9a9a;">${this.selectedCompanyName}</strong> has reached its maximum participant limit.
                                <br><strong>Limit:</strong> ${d.max_pax} &nbsp;|&nbsp; <strong>Registered:</strong> ${d.current_pax}
                                <br><br>No more walk-in registrations are allowed for this company.
                            </div>`,
                            icon: 'error',
                            customClass: { confirmButton: 'orange-button' },
                        });
                        this.resetToScan();
                    } else {
                        Swal.fire({
                            title: 'Registration Failed',
                            text: 'Something went wrong. Please try again.',
                            icon: 'error',
                            customClass: { confirmButton: 'orange-button' },
                        });
                    }
                    this.submittingRegistration = false;
                    return;
                }
                this.submittingRegistration = false;
            }

            // andFinish = true — show summary and reset
            await Swal.fire({
                title: 'Registration Complete!',
                html: `
                    <div style="text-align:center; margin-top:4px;">
                        <strong style="font-size:1.05rem;">${this.selectedCompanyName}</strong>
                        <div style="font-size:0.85rem; color:rgba(255,255,255,0.6); margin-top:6px;">has been successfully registered.</div>
                    </div>
                `,
                icon: 'success',
                customClass: { confirmButton: 'orange-button' },
                confirmButtonText: 'Done',
            });
            this.resetToScan();
        },

        async submitGuestQrRegistration(uniqueCode) {
            try {
                const response = await axios.post(process.env.VUE_APP_API_HOST + '/api/registerGuestByQr', {
                    unique_code: uniqueCode,
                });
                this._showGuestScanSwal(response.data).then(() => {
                    this.guestCameraActive = true;
                    this.$nextTick(() => setTimeout(() => this._startGuestScanner(false), 400));
                });
            } catch (error) {
                await this._handleGuestQrError(error);
                this.guestCameraActive = true;
                this.$nextTick(() => setTimeout(() => this._startGuestScanner(false), 400));
            }
        },

        _handleGuestQrError(error) {
            const status = error?.response?.status;
            const data   = error?.response?.data ?? {};

            if (status === 422 && data.error_code === 'PAX_LIMIT_EXCEEDED') {
                return Swal.fire({
                    title: '<strong>Company Limit Reached!</strong>',
                    html: `
                        <div style="text-align:center;">
                            <div style="font-size:1rem; margin-bottom:10px;">
                                <strong style="color:#ef9a9a;">${data.company_name}</strong> has reached its maximum participant limit.
                            </div>
                            <div style="display:flex; justify-content:center; gap:16px; margin-bottom:10px;">
                                <div style="background:rgba(244,67,54,0.12); border:1px solid rgba(244,67,54,0.3); border-radius:8px; padding:8px 18px;">
                                    <div style="font-size:0.65rem; color:rgba(255,255,255,0.45); letter-spacing:0.8px; text-transform:uppercase;">Max Allowed</div>
                                    <div style="font-size:1.4rem; font-weight:700; color:#ef9a9a;">${data.max_pax}</div>
                                </div>
                                <div style="background:rgba(79,195,247,0.10); border:1px solid rgba(79,195,247,0.25); border-radius:8px; padding:8px 18px;">
                                    <div style="font-size:0.65rem; color:rgba(255,255,255,0.45); letter-spacing:0.8px; text-transform:uppercase;">Registered</div>
                                    <div style="font-size:1.4rem; font-weight:700; color:#4fc3f7;">${data.current_pax}</div>
                                </div>
                            </div>
                            <div style="font-size:0.8rem; color:rgba(255,255,255,0.5);">
                                Guest: <strong style="color:#fff;">${data.full_name || 'N/A'}</strong>
                            </div>
                            <div style="margin-top:8px; font-size:0.78rem; color:rgba(255,255,255,0.4);">
                                No further registrations are allowed for this company.
                            </div>
                        </div>
                    `,
                    icon: 'error',
                    customClass: { confirmButton: 'orange-button' },
                    confirmButtonText: 'OK',
                });
            }

            if (status === 404) {
                return Swal.fire({
                    title: '<strong><u>QR CODE NOT VALID</u></strong>',
                    text: 'No registered guest found for this QR code.',
                    icon: 'warning',
                    customClass: { confirmButton: 'orange-button' },
                    confirmButtonText: 'OK',
                });
            }

            return Swal.fire({
                title: 'Error',
                text: 'Could not process the QR code. Please try again.',
                icon: 'error',
                customClass: { confirmButton: 'orange-button' },
                confirmButtonText: 'OK',
            });
        },

        _showGuestScanSwal(data) {
            if (!data || typeof data !== 'object' || !data.message || !data.icon) {
                return Swal.fire({
                    title: '<strong><u>QR CODE NOT VALID</u></strong>',
                    text: 'No registered guest found for this QR code.',
                    icon: 'warning',
                    customClass: { confirmButton: 'orange-button' },
                    confirmButtonText: 'OK',
                });
            }

            const tableRow = data.table_num && data.table_num !== 'N/A'
                ? `<br><strong>Table:</strong> ${data.table_num}`
                : '';

            return Swal.fire({
                title: data.message,
                html: `
                    <div style="text-align: center;">
                        <strong>Guest Name:</strong> ${data.full_name}<br>
                        <strong>Company:</strong> ${data.company_name}${tableRow}
                    </div>
                `,
                icon: data.icon,
                customClass: { confirmButton: 'orange-button' },
                confirmButtonText: 'OK',
            });
        },


        onCompanyCodeInput(idx) {
            const val = this.companyCodeDigits[idx];
            if (val && val.length > 0) {
                this.companyCodeDigits[idx] = val.slice(-1).toUpperCase();
                if (idx < 5) {
                    const nextRef = this.$refs['ccBox' + (idx + 1)];
                    if (nextRef && nextRef[0]) nextRef[0].focus();
                }
            }
        },

        onCompanyCodeBackspace(idx, event) {
            if (!this.companyCodeDigits[idx] && idx > 0) {
                event.preventDefault();
                this.$set(this.companyCodeDigits, idx - 1, '');
                const prevRef = this.$refs['ccBox' + (idx - 1)];
                if (prevRef && prevRef[0]) prevRef[0].focus();
            }
        },

        onCompanyCodePaste(event) {
            const pasted = (event.clipboardData || window.clipboardData).getData('text').trim().toUpperCase();
            const chars = pasted.split('').slice(0, 6);
            chars.forEach((ch, idx) => {
                this.$set(this.companyCodeDigits, idx, ch);
            });
            const focusIdx = Math.min(chars.length, 5);
            const targetRef = this.$refs['ccBox' + focusIdx];
            if (targetRef && targetRef[0]) targetRef[0].focus();
        },

        async submitCompanyCode() {
            const code = this.companyCodeValue;
            if (code.length < 6) return;
            this.submittingCompanyCode = true;
            try {
                this.submitEmployeeId(code);
            } finally {
                this.submittingCompanyCode = false;
                this.companyCodeDigits = ['', '', '', '', '', ''];
            }
        },

        onUniqueCodeInput(idx) {
            const val = this.uniqueCodeDigits[idx];
            if (val && val.length > 0) {
                this.uniqueCodeDigits[idx] = val.slice(-1);
                if (idx < 5) {
                    const nextRef = this.$refs['ucBox' + (idx + 1)];
                    if (nextRef && nextRef[0]) nextRef[0].focus();
                }
            }
        },

        onUniqueCodeBackspace(idx, event) {
            if (!this.uniqueCodeDigits[idx] && idx > 0) {
                event.preventDefault();
                this.$set(this.uniqueCodeDigits, idx - 1, '');
                const prevRef = this.$refs['ucBox' + (idx - 1)];
                if (prevRef && prevRef[0]) prevRef[0].focus();
            }
        },

        onUniqueCodePaste(event) {
            const pasted = (event.clipboardData || window.clipboardData).getData('text').trim();
            const chars = pasted.split('').slice(0, 6);
            chars.forEach((ch, idx) => {
                this.$set(this.uniqueCodeDigits, idx, ch);
            });
            const focusIdx = Math.min(chars.length, 5);
            const targetRef = this.$refs['ucBox' + focusIdx];
            if (targetRef && targetRef[0]) targetRef[0].focus();
        },

        async submitUniqueCode() {
            const code = this.uniqueCodeValue;
            if (code.length < 6) return;

            this.submittingGuestCode = true;
            try {
                const response = await axios.post(process.env.VUE_APP_API_HOST + '/api/registerGuestByQr', {
                    unique_code: code,
                });

                this.uniqueCodeDigits = ['', '', '', '', '', ''];
                await this._showGuestScanSwal(response.data);
                this.guestCameraActive = true;
                this.$nextTick(() => setTimeout(() => this._startGuestScanner(false), 400));
            } catch (error) {
                await this._handleGuestQrError(error);
                this.uniqueCodeDigits = ['', '', '', '', '', ''];
            } finally {
                this.submittingGuestCode = false;
            }
        },

        getCompanyDropdowns() {
            this.$store.dispatch('urlGet', { url: 'companyRoute' }).then((res) => {
                this.dropdowns.company_list = res.data;
            });
        },

        _startScanner(useAnyCamera) {
            const qrRegionId = "qr-code-full-region";

            if (!document.getElementById(qrRegionId)) {
                setTimeout(() => this._startScanner(useAnyCamera), 300);
                return;
            }

            if (this.html5QrCode) {
                try { this.html5QrCode.stop(); } catch (_err) {}
            }

            this.html5QrCode = new Html5Qrcode(qrRegionId);
            this.qrScannerRunning = true;
            this.scanReady = false;

            const cameraConstraint = useAnyCamera ? true : { facingMode: "environment" };

            this.html5QrCode.start(
                cameraConstraint,
                { fps: 10, qrbox: 250 },
                (decodedText) => {
                    if (!this.scanReady) return;

                    this.html5QrCode.stop().then(() => {
                        this.qrScannerRunning = false;
                        this.submitEmployeeId(decodedText);
                    }).catch(err => console.error("Failed to stop scanner", err));
                },
                () => {}
            ).then(() => {
                setTimeout(() => { this.scanReady = true; }, 1500);
            }).catch(err => {
                console.error("Failed to start QR scanner", err);

                if (!useAnyCamera && (
                    err?.name === 'OverconstrainedError' ||
                    err?.name === 'NotFoundError' ||
                    err?.name === 'NotReadableError'
                )) {
                    this._startScanner(true);
                    return;
                }

                this.qrScannerRunning = false;
                Swal.fire({
                    title: 'Camera Error',
                    text: err?.message || 'Unable to access camera. Check permissions and ensure the page is on HTTPS.',
                    icon: 'error',
                    customClass: { confirmButton: 'orange-button' },
                });
            });
        },

        _startGuestScanner(useAnyCamera) {
            const qrRegionId = "qr-code-guest-region";

            if (!document.getElementById(qrRegionId)) {
                setTimeout(() => this._startGuestScanner(useAnyCamera), 300);
                return;
            }

            if (this.html5QrCodeGuest) {
                try { this.html5QrCodeGuest.stop(); } catch (_err) {}
            }

            this.html5QrCodeGuest = new Html5Qrcode(qrRegionId);
            this.guestScanReady = false;

            const cameraConstraint = useAnyCamera ? true : { facingMode: "environment" };

            this.html5QrCodeGuest.start(
                cameraConstraint,
                { fps: 10, qrbox: 250 },
                (decodedText) => {
                    if (!this.guestScanReady) return;

                    this.html5QrCodeGuest.stop().then(() => {
                        this.guestScanReady = false;
                        this.submitGuestQrRegistration(decodedText);
                    }).catch(err => console.error("Failed to stop guest scanner", err));
                },
                () => {}
            ).then(() => {
                setTimeout(() => { this.guestScanReady = true; }, 1500);
            }).catch(err => {
                console.error("Failed to start guest QR scanner", err);

                if (!useAnyCamera && (
                    err?.name === 'OverconstrainedError' ||
                    err?.name === 'NotFoundError' ||
                    err?.name === 'NotReadableError'
                )) {
                    this._startGuestScanner(true);
                    return;
                }

                Swal.fire({
                    title: 'Camera Error',
                    text: err?.message || 'Unable to access camera. Check permissions and ensure the page is on HTTPS.',
                    icon: 'error',
                    customClass: { confirmButton: 'orange-button' },
                });
            });
        },

		onScanSucess(decodeResult) {
            this.scannedqrcodes = decodeResult;
            this.qrScannerRunning = false;
            this.submitEmployeeId(this.scannedqrcodes);
		},
		toggleUploadModal() {
			this.uploadModalOpen = !this.uploadModalOpen;
		},
		uploadFile() {
			this.toggleUploadModal();
		},
		cancelUpload() {
		    this.openDialog();
		},
		reloadPage() {
            window.location.reload();
        },
        closeQRScanner() {
            this.closeDialog();
        },
		async handleFileUpload(event) {
            const file = event.target.files[0];
            if (!file) return;

            const reader = new FileReader();

            reader.onload = async () => {
                const image = new Image();
                image.src = reader.result;

                image.onload = async () => {
                const canvas = document.createElement('canvas');
                canvas.width = image.width;
                canvas.height = image.height;

                const ctx = canvas.getContext('2d');
                ctx.drawImage(image, 0, 0);

                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const qrCode = jsQR(imageData.data, imageData.width, imageData.height);

                if (qrCode) {
                    this.scannedqrcodes = qrCode.data;
                    this.submitEmployeeId();
                } else {
                    console.error('QR code not found or could not be decoded.');
                }

                this.uploadModalOpen = false;
                };
            };

            reader.readAsDataURL(file);
        },
		chooseFile() {
		    this.$refs.fileInput.click();
		},
		handleFileChange(event) {
            let file = event.target.files[0];
            this.selectedFile = file;
            this.selectedFileName = file ? file.name : null;
		},
		clearFile() {
            this.selectedFile = null;
            this.selectedFileName = null;
		},
		clearEmployeeId() {
		    this.employeeId = '';
		},
        checkImagePathLogo(image_path) {
            return process.env.VUE_APP_API_HOST + "/" + image_path
        },
	},
	created() {
        document.title = 'Confirm Registration';
        this.getCompanyDropdowns();
        }
	}
</script>

<style>
	@import '/src/assets/css/HelloWorld.css';

    .transition-scanner {
        transition: opacity 0.5s ease, transform 0.5s ease;
        opacity: 1;
        transform: translateY(0);
    }
    .transition-scanner.hidden {
        opacity: 0;
        transform: translateY(20px);
        pointer-events: none;
    }

    /* ── Scan type toggle bar ── */
    .scan-type-toggle-bar {
        display: flex;
        justify-content: center;
        align-items: center;
        background: #0d1f42;
        padding: 10px 16px;
        border-bottom: 1px solid rgba(255,255,255,0.08);
    }

    .scan-type-btn {
        font-size: 0.72rem !important;
        font-weight: 700 !important;
        letter-spacing: 0.5px !important;
        text-transform: none !important;
        color: rgba(255,255,255,0.65) !important;
        background: rgba(255,255,255,0.06) !important;
        border-color: rgba(79, 195, 247, 0.25) !important;
    }

    .scan-type-btn.v-btn--active {
        background: #17468f !important;
        color: #fff !important;
    }

    /* ── Scan divider ── */
    .scan-divider-row {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 0 24px;
        color: #aaa;
    }
    .scan-divider-text {
        font-size: 0.72rem;
        font-weight: 700;
        letter-spacing: 1px;
        color: #bbb;
        white-space: nowrap;
    }

    /* ── Guest Name Dialog ── */
    .guest-dialog-card {
        border-radius: 16px !important;
        overflow: hidden;
        font-family: Evogria, sans-serif;
        background: #12213f !important;
    }

    .guest-dialog-header {
        background: linear-gradient(135deg, #17468f 0%, #1e6fc4 100%);
        padding: 20px 24px 18px;
        display: flex;
        align-items: center;
        gap: 16px;
    }

    .guest-scan-header {
        background: linear-gradient(135deg, #0d3364 0%, #155fa0 100%);
    }

    .guest-dialog-icon-wrap {
        background: rgba(255, 255, 255, 0.18);
        border-radius: 50%;
        width: 64px;
        height: 64px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        border: 2px solid rgba(255,255,255,0.35);
    }

    .guest-dialog-header-text {
        color: #fff;
    }

    .guest-dialog-title {
        font-size: 1.4rem;
        font-weight: 700;
        letter-spacing: 1px;
        line-height: 1.2;
    }

    .guest-dialog-subtitle {
        font-size: 0.78rem;
        opacity: 0.75;
        letter-spacing: 0.5px;
        margin-top: 2px;
    }

    .guest-dialog-company-row {
        display: flex;
        align-items: center;
        padding: 12px 24px 10px;
        background: #0d1f42;
    }

    .guest-dialog-company-label {
        font-size: 0.8rem;
        color: rgba(255,255,255,0.6);
        font-weight: 600;
        letter-spacing: 0.3px;
    }

    /* ── Name guide label ── */
    .name-guide-label {
        display: flex;
        align-items: center;
        font-size: 0.72rem;
        color: rgba(255,255,255,0.5);
        letter-spacing: 0.3px;
    }

    .name-guide-label strong {
        color: #4fc3f7;
        margin-left: 3px;
    }

    /* ── Parsed name preview ── */
    .parsed-name-preview {
        background: rgba(23, 70, 143, 0.25);
        border: 1px solid rgba(79, 195, 247, 0.2);
        border-radius: 8px;
        padding: 10px 14px;
    }

    .parsed-name-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 2px 0;
    }

    .parsed-name-label {
        font-size: 0.7rem;
        color: rgba(255,255,255,0.45);
        font-weight: 600;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        min-width: 90px;
    }

    .parsed-name-value {
        font-size: 0.82rem;
        color: #ffffff;
        font-weight: 600;
        letter-spacing: 0.3px;
        text-align: right;
    }

    /* ── Shared input field style ── */
    .dialog-input-field .v-input__slot {
        border-radius: 10px !important;
        background: rgba(255, 255, 255, 0.06) !important;
    }

    .dialog-input-field .v-input__slot fieldset {
        border-color: rgba(79, 195, 247, 0.4) !important;
    }

    .dialog-input-field.v-input--is-focused .v-input__slot fieldset {
        border-color: #4fc3f7 !important;
        border-width: 2px !important;
    }

    .dialog-input-field input,
    .dialog-input-field .v-select__selection {
        color: #ffffff !important;
        font-size: 1rem;
        letter-spacing: 0.3px;
    }

    .dialog-input-field input::placeholder {
        color: rgba(255, 255, 255, 0.3) !important;
    }

    .dialog-input-field .v-label {
        color: rgba(255, 255, 255, 0.5) !important;
    }

    .dialog-input-field.v-input--is-focused .v-label {
        color: #4fc3f7 !important;
    }

    .dialog-input-field .v-input__prepend-inner .v-icon,
    .dialog-input-field .v-input__append-inner .v-icon {
        color: rgba(79, 195, 247, 0.75) !important;
    }

    .guest-name-field .v-input__slot {
        border-radius: 10px !important;
    }

    .guest-dialog-hint {
        font-size: 0.72rem;
        color: rgba(255, 255, 255, 0.4);
        display: flex;
        align-items: center;
        gap: 4px;
    }

    .guest-dialog-hint kbd {
        background: rgba(79, 195, 247, 0.12);
        border-radius: 4px;
        padding: 1px 6px;
        font-size: 0.7rem;
        color: #4fc3f7;
        border: 1px solid rgba(79, 195, 247, 0.35);
    }

    .guest-dialog-cancel-btn {
        border-radius: 8px !important;
        text-transform: none !important;
        font-weight: 600;
        letter-spacing: 0.3px;
    }

    .guest-dialog-submit-btn {
        border-radius: 8px !important;
        text-transform: none !important;
        font-weight: 700;
        letter-spacing: 0.5px;
        padding: 0 20px !important;
        height: 42px !important;
    }


    /* ── Guest scan status bar ── */
    .guest-scan-status {
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.75rem;
        color: rgba(255,255,255,0.5);
        letter-spacing: 0.4px;
        padding: 4px 0 10px;
    }

    /* ── Guest scan result card ── */
    .guest-scan-result-card {
        background: rgba(23, 70, 143, 0.3);
        border: 1px solid rgba(79, 195, 247, 0.25);
        border-radius: 10px;
        padding: 12px 16px;
    }

    .guest-scan-result-row {
        display: flex;
        align-items: center;
        margin-bottom: 8px;
    }

    .guest-scan-result-message {
        font-size: 0.88rem;
        font-weight: 700;
        color: #fff;
        letter-spacing: 0.3px;
    }

    .guest-scan-result-detail {
        display: flex;
        justify-content: space-between;
        padding: 2px 0;
    }

    .guest-scan-result-label {
        font-size: 0.7rem;
        color: rgba(255,255,255,0.45);
        font-weight: 600;
        letter-spacing: 0.5px;
        text-transform: uppercase;
    }

    .guest-scan-result-value {
        font-size: 0.82rem;
        color: #fff;
        font-weight: 600;
        text-align: right;
    }

    /* ── Unique code input boxes ── */
    .unique-code-label {
        text-align: center;
        font-size: 0.72rem;
        font-weight: 700;
        letter-spacing: 2px;
        color: rgba(255,255,255,0.5);
        text-transform: uppercase;
    }

    .unique-code-boxes {
        display: flex;
        justify-content: center;
        gap: 10px;
    }

    .unique-code-box {
        width: 46px;
        height: 54px;
        background: #1a2a4a;
        border: 2px solid #4fc3f7;
        border-radius: 8px;
        text-align: center;
        font-size: 1.3rem;
        font-weight: 700;
        color: #ffffff;
        caret-color: #4fc3f7;
        outline: none;
        transition: border-color 0.2s, box-shadow 0.2s;
        font-family: monospace;
    }

    .unique-code-box:focus {
        border-color: #81d4fa;
        box-shadow: 0 0 0 3px rgba(79, 195, 247, 0.2);
    }

    /* ── Pax number field ── */
    .pax-number-field input[type='number']::-webkit-inner-spin-button,
    .pax-number-field input[type='number']::-webkit-outer-spin-button {
        opacity: 1;
        height: 32px;
    }

    /* ── Existing guests panel ── */
    .existing-guests-wrap {
        background: rgba(23,70,143,0.18);
        border: 1px solid rgba(79,195,247,0.2);
        border-radius: 10px;
        padding: 10px 12px;
    }

    .existing-guests-label {
        font-size: 0.68rem;
        font-weight: 700;
        color: #4fc3f7;
        text-transform: uppercase;
        letter-spacing: 0.8px;
        margin-bottom: 6px;
        display: flex;
        align-items: center;
    }

    .existing-guests-list {
        max-height: 150px;
        overflow-y: auto;
    }

    .existing-guests-list::-webkit-scrollbar {
        width: 4px;
    }

    .existing-guests-list::-webkit-scrollbar-thumb {
        background: rgba(79,195,247,0.3);
        border-radius: 2px;
    }

    .existing-guest-item {
        font-size: 0.78rem;
        color: rgba(255,255,255,0.8);
        padding: 3px 0;
        display: flex;
        align-items: center;
        letter-spacing: 0.2px;
        border-bottom: 1px solid rgba(255,255,255,0.05);
    }

    .existing-guest-item:last-child {
        border-bottom: none;
    }

    .existing-guest-item--pending {
        cursor: pointer;
        transition: background 0.15s ease;
        border-radius: 4px;
        padding-left: 4px;
        padding-right: 4px;
    }

    .existing-guest-item--pending:hover {
        background: rgba(255, 165, 0, 0.1);
    }

    .existing-guest-item--selected {
        background: rgba(129, 199, 132, 0.15) !important;
        color: #e8f5e9 !important;
    }

    .existing-guest-item--confirming {
        opacity: 0.7;
        pointer-events: none;
    }

    /* ── Pax progress ── */
    .pax-progress-text {
        font-size: 0.72rem;
        font-weight: 700;
        color: #4fc3f7;
        letter-spacing: 0.5px;
    }

    /* ── Unified guest roster ── */
    .guest-roster {
        background: rgba(10, 20, 48, 0.55);
        border: 1px solid rgba(79,195,247,0.18);
        border-radius: 10px;
        overflow: hidden;
    }

    .guest-roster-header {
        display: flex;
        align-items: center;
        padding: 7px 12px;
        background: rgba(23,70,143,0.35);
        font-size: 0.67rem;
        font-weight: 700;
        color: #4fc3f7;
        text-transform: uppercase;
        letter-spacing: 0.8px;
        border-bottom: 1px solid rgba(79,195,247,0.12);
    }

    .guest-roster-count {
        color: rgba(255,255,255,0.4);
        font-weight: 600;
    }

    .guest-roster-list {
        max-height: 140px;
        overflow-y: auto;
    }

    .guest-roster-list::-webkit-scrollbar {
        width: 3px;
    }

    .guest-roster-list::-webkit-scrollbar-thumb {
        background: rgba(79,195,247,0.25);
        border-radius: 2px;
    }

    .guest-roster-item {
        display: flex;
        align-items: center;
        padding: 5px 12px;
        border-bottom: 1px solid rgba(255,255,255,0.04);
        gap: 4px;
    }

    .guest-roster-item:last-child {
        border-bottom: none;
    }

    .guest-roster-new {
        background: rgba(76,175,80,0.08);
    }

    .guest-roster-prev {
        background: transparent;
    }

    .guest-roster-badge {
        font-size: 0.55rem;
        font-weight: 800;
        letter-spacing: 0.6px;
        border-radius: 3px;
        padding: 1px 4px;
        flex-shrink: 0;
        line-height: 1.4;
    }

    .guest-roster-badge-new {
        background: rgba(76,175,80,0.25);
        color: #81c784;
        border: 1px solid rgba(76,175,80,0.4);
    }

    .guest-roster-badge-prev {
        background: rgba(144,164,174,0.15);
        color: #90a4ae;
        border: 1px solid rgba(144,164,174,0.3);
    }

    .guest-roster-name {
        font-size: 0.77rem;
        color: rgba(255,255,255,0.82);
        font-weight: 500;
        letter-spacing: 0.2px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .guest-roster-new .guest-roster-name {
        color: #fff;
        font-weight: 600;
    }

    /* ── Pax limit indicator chips ── */
    .pax-limit-row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 4px;
    }

    .pax-limit-chip {
        font-size: 0.65rem !important;
        font-weight: 700 !important;
        letter-spacing: 0.4px !important;
        border-radius: 6px !important;
        height: 22px !important;
    }

    .pax-limit-chip--total {
        background: rgba(255, 193, 7, 0.12) !important;
        color: #ffc107 !important;
        border: 1px solid rgba(255, 193, 7, 0.3) !important;
    }

    .pax-limit-chip--registered {
        background: rgba(79, 195, 247, 0.12) !important;
        color: #4fc3f7 !important;
        border: 1px solid rgba(79, 195, 247, 0.3) !important;
    }

    .pax-limit-chip--available {
        background: rgba(76, 175, 80, 0.12) !important;
        color: #81c784 !important;
        border: 1px solid rgba(76, 175, 80, 0.3) !important;
    }

    .pax-limit-chip--full {
        background: rgba(244, 67, 54, 0.15) !important;
        color: #ef9a9a !important;
        border: 1px solid rgba(244, 67, 54, 0.35) !important;
    }

    .guest-roster-item--pending {
        cursor: pointer;
        transition: background 0.15s ease;
    }

    .guest-roster-item--pending:hover {
        background: rgba(129, 199, 132, 0.12);
    }

    .guest-roster-item--confirming {
        opacity: 0.7;
        pointer-events: none;
    }

    .guest-roster-badge-pending {
        background: rgba(129, 199, 132, 0.2);
        color: #81c784;
    }
</style>
